/**
 * Synthesize a minimal event stream from a `SampleSkeleton`.
 *
 * The legacy transcript presentation layer (timeline swimlanes, main-view
 * selection, outline) is a function of the full event list. A chunked
 * sample never loads all events, but the skeleton carries exactly the
 * structure that layer consumes: the span tree, per-gap model counts,
 * persisted notables (score/checkpoint) with exact positions, per-span
 * direct-child event-type counts, and real span timestamps. This module
 * rebuilds a stand-in event stream from that structure so the REAL
 * pipeline (see `app/samples/transcript/chunked/mainViewOutline.ts`) can
 * run unchanged — no ported twin to drift.
 *
 * Fidelity contract (what is and isn't reconstructed):
 *
 * - Span/step structure, model counts per gap, and notable positions are
 *   exact — outline rows, turn counts, and scoring rows match the legacy
 *   pipeline on well-formed logs (pinned by `mainViewOutline.test.ts`).
 * - Span/step begin and end timestamps are the real ones (skeleton `t`);
 *   event timestamps are interpolated evenly inside their gap's real time
 *   bounds. Ordering is faithful; sub-span timing detail is smoothed, so
 *   time-proportional displays (swimlane bars) are approximate between
 *   span boundaries.
 * - Non-model, non-notable direct-child events ("strays": logger, info,
 *   sandbox, tool, ...) have known counts but unknown positions; one
 *   representative per type is emitted at its span's begin, carrying the
 *   minimum payload the outline pipeline dereferences. They exist to keep
 *   `filterEmpty` survival and type-filter behavior faithful. Known
 *   deviation: types that DO yield outline rows (subtask,
 *   error/compaction/sample_limit) collapse to one row per span, anchored
 *   at the span start rather than their true positions. Notable types past
 *   the persistence cap emit no stray at all — the persisted ones already
 *   cover survival, and an extra would add a row the real pipeline lacks.
 * - Root-level plain events (e.g. a legacy log's `sample_init`, an
 *   interrupted eval's `sample_limit`) are absent: the skeleton records
 *   direct-child type counts per span only, and root notables are the only
 *   root-level events persisted.
 * - Event payloads are empty (no model input/output), so content-based
 *   utility-call detection (`wrapUtilityEvents`' system-prompt comparison)
 *   never fires. Utility model calls therefore count as ordinary turns.
 *
 * Every synthetic event carries a unique `uuid`; `ordinals` maps it to the
 * best-known index in the real event sequence for scroll anchoring (models
 * anchor at their gap's lower bound — the same convention the parity
 * harness signed off as allowance class 3). Skeleton span ids are also
 * seeded (span.begin), since timeline-derived outline span rows anchor by
 * span id rather than synthetic uuid.
 */
import { normalizeEvent } from "@tsmono/inspect-common/normalize";
import type { Event } from "@tsmono/inspect-common/types";

import { formatPyTimestamp, parsePyTimestamp } from "./pyTimestamp";
import type { SampleSkeleton, SkeletonNotable, SkeletonSpan } from "./types";

export interface SyntheticStream {
  events: Event[];
  /** Synthetic event uuid -> ordinal in the real event sequence. */
  ordinals: ReadonlyMap<string, number>;
}

/** Legacy step pairs are skeleton spans with a synthesized `step-<i>` id. */
const isStepSpan = (span: SkeletonSpan): boolean => /^step-\d+$/.test(span.id);

/**
 * Single choke point for constructing events the pipeline treats as real.
 * The synthesized objects carry every field the transcript pipeline reads
 * (see module docstring); normalizeEvent fills the remaining type-required
 * fields (config, output defaults, ...) the pipeline may read unguarded.
 */
const synth = (fields: Record<string, unknown>): Event => {
  const event = normalizeEvent(fields);
  if (event === undefined) {
    throw new Error("synthetic event payload must carry an `event` tag");
  }
  return event;
};

export const syntheticEventsFromSkeleton = (
  skel: SampleSkeleton
): SyntheticStream => {
  const events: Event[] = [];
  const ordinals = new Map<string, number>();

  const spans = skel.spans;
  const childSpans = new Map<number | null, number[]>();
  const spanNotables = new Map<number | null, SkeletonNotable[]>();
  const append = <K, V>(map: Map<K, V[]>, key: K, value: V) => {
    const list = map.get(key);
    if (list === undefined) {
      map.set(key, [value]);
    } else {
      list.push(value);
    }
  };
  spans.forEach((span, index) => {
    append(childSpans, span.parent ?? null, index);
  });
  for (const notable of skel.notables) {
    append(spanNotables, notable.span ?? null, notable);
  }

  const beginsUs = spans.map((span) => parsePyTimestamp(span.t[0]).epochUs);
  const endsUs = spans.map((span) => parsePyTimestamp(span.t[1]).epochUs);
  const beginUs = (index: number): number => beginsUs[index] ?? 0;
  const endUs = (index: number): number => endsUs[index] ?? 0;

  /** Nearest non-step ancestor's span id (steps have no span identity). */
  const realSpanId = (index: number | null): string | null => {
    let current = index;
    while (current !== null) {
      const span = spans[current];
      if (span === undefined) return null;
      if (!isStepSpan(span)) return span.id;
      current = span.parent ?? null;
    }
    return null;
  };

  const push = (event: Event, uuid: string, ordinal: number) => {
    events.push(event);
    ordinals.set(uuid, ordinal);
  };

  const common = (uuid: string, spanId: string | null, us: number) => ({
    timestamp: formatPyTimestamp(us),
    pending: false,
    working_start: 0,
    uuid,
    span_id: spanId,
    metadata: null,
  });

  const emitModel = (
    spanIdx: number | null,
    gap: number,
    k: number,
    ordinal: number,
    us: number
  ) => {
    const uuid = `synth-model-${spanIdx ?? "root"}-${gap}-${k}`;
    push(
      synth({
        event: "model",
        model: "",
        input: [],
        output: { model: "", choices: [], usage: null },
        ...common(uuid, realSpanId(spanIdx), us),
      }),
      uuid,
      ordinal
    );
  };

  const emitNotable = (
    notable: SkeletonNotable,
    spanId: string | null,
    us: number
  ) => {
    const uuid = `synth-notable-${notable.i}`;
    const fields =
      notable.type === "score"
        ? {
            event: "score",
            score: {
              value: "",
              answer: null,
              explanation: null,
              metadata: null,
            },
            intermediate: false,
          }
        : notable.type === "checkpoint"
          ? { event: "checkpoint", checkpoint_id: notable.checkpoint_id }
          : { event: notable.type };
    push(synth({ ...fields, ...common(uuid, spanId, us) }), uuid, notable.i);
  };

  /** Minimum payload each stray type needs to survive the outline pipeline
   * (labelers/summarizers dereference these fields without guards). */
  const strayPayload = (type: string, uuid: string): Record<string, unknown> =>
    type === "tool"
      ? { id: uuid, function: "", agent: null, events: [], result: null }
      : type === "subtask"
        ? { name: "subtask", input: {}, result: null, events: [] }
        : type === "score"
          ? {
              score: {
                value: "",
                answer: null,
                explanation: null,
                metadata: null,
              },
              intermediate: false,
            }
          : type === "checkpoint"
            ? { checkpoint_id: "" }
            : {};

  const emitStrays = (spanIdx: number, us: number) => {
    const span = spans[spanIdx];
    if (span === undefined) return;
    const notableCounts = new Map<string, number>();
    for (const notable of spanNotables.get(spanIdx) ?? []) {
      notableCounts.set(
        notable.type,
        (notableCounts.get(notable.type) ?? 0) + 1
      );
    }
    const spanId = realSpanId(spanIdx);
    for (const [type, count] of Object.entries(span.children)) {
      if (type === "model") continue;
      const persisted = notableCounts.get(type) ?? 0;
      if (count - persisted <= 0) continue;
      // Capped notables: the persisted ones already guarantee survival and
      // type-filter fidelity; an extra placeholder would add an outline row
      // the real pipeline doesn't have (e.g. an unmerged "scoring" row).
      if (persisted > 0) continue;
      const uuid = `synth-stray-${spanIdx}-${type}`;
      push(
        synth({
          event: type,
          ...strayPayload(type, uuid),
          ...common(uuid, spanId, us),
        }),
        uuid,
        span.begin + 1
      );
    }
  };

  /**
   * Emit a container's gap models + items (child spans and notables).
   *
   * Timing: child spans carry real begin/end times; the loose points
   * between them (gap models + notables) are spaced evenly inside the
   * segment's real time bounds. Inverted bounds (cross-context span exits)
   * degrade to the segment's lower bound — order still holds because
   * `buildSpanTree`'s child sort is stable.
   */
  const emitContents = (spanIdx: number | null, loUs: number, hiUs: number) => {
    const span = spanIdx !== null ? spans[spanIdx] : undefined;
    const children = childSpans.get(spanIdx) ?? [];
    const notables = spanNotables.get(spanIdx) ?? [];
    type Item =
      | { kind: "span"; index: number; at: number }
      | { kind: "notable"; notable: SkeletonNotable; at: number };
    const items: Item[] = [
      ...children.map((index): Item => {
        const child = spans[index];
        return { kind: "span", index, at: child?.begin ?? 0 };
      }),
      ...notables.map((notable): Item => ({
        kind: "notable",
        notable,
        at: notable.i,
      })),
    ].sort((a, b) => a.at - b.at);

    // gap_models has length items+1; the root container has no record —
    // top-level phase spans cover all models, so root gaps are empty.
    const gaps = span?.gap_models ?? [];
    const gapLowerBound = (g: number): number => {
      if (g === 0) return span === undefined ? 0 : span.begin + 1;
      const prev = items[g - 1];
      if (prev === undefined) return span?.begin ?? 0;
      return prev.kind === "span"
        ? (spans[prev.index]?.extent[1] ?? prev.at) + 1
        : prev.at + 1;
    };

    // A segment is a maximal run of loose points (gap models + notables)
    // between two child spans (or the container bounds); points inside it
    // share evenly interpolated timestamps.
    let g = 0;
    let segmentLoUs = loUs;
    while (g <= items.length) {
      type Point =
        | { kind: "model"; gap: number; k: number; ordinal: number }
        | { kind: "notable"; notable: SkeletonNotable };
      const points: Point[] = [];
      let nextSpan: number | undefined;
      while (g <= items.length) {
        const count = gaps[g] ?? 0;
        const ordinal = gapLowerBound(g);
        for (let k = 0; k < count; k++) {
          points.push({ kind: "model", gap: g, k, ordinal });
        }
        const item = items[g];
        g++;
        if (item === undefined) break;
        if (item.kind === "span") {
          nextSpan = item.index;
          break;
        }
        points.push({ kind: "notable", notable: item.notable });
      }

      const segmentHiUs = nextSpan !== undefined ? beginUs(nextSpan) : hiUs;
      const step =
        segmentHiUs > segmentLoUs
          ? (segmentHiUs - segmentLoUs) / (points.length + 1)
          : 0;
      points.forEach((point, i) => {
        const us = Math.round(segmentLoUs + step * (i + 1));
        if (point.kind === "model") {
          emitModel(spanIdx, point.gap, point.k, point.ordinal, us);
        } else {
          emitNotable(point.notable, realSpanId(spanIdx), us);
        }
      });

      if (nextSpan !== undefined) {
        emitSpan(nextSpan);
        segmentLoUs = Math.max(segmentLoUs, endUs(nextSpan));
      }
    }
  };

  const emitSpan = (spanIdx: number) => {
    const span = spans[spanIdx];
    if (span === undefined) return;
    const loUs = beginUs(spanIdx);
    const hiUs = endUs(spanIdx);
    const beginUuid = `synth-begin-${spanIdx}`;
    const endUuid = `synth-end-${spanIdx}`;
    const step = isStepSpan(span);
    const stepFields = (action: "begin" | "end") => ({
      event: "step",
      action,
      name: span.name,
      type: span.type ?? null,
    });
    push(
      synth(
        step
          ? {
              ...stepFields("begin"),
              ...common(beginUuid, realSpanId(span.parent ?? null), loUs),
            }
          : {
              event: "span_begin",
              id: span.id,
              parent_id: realSpanId(span.parent ?? null),
              name: span.name,
              type: span.type ?? null,
              ...common(beginUuid, span.id, loUs),
            }
      ),
      beginUuid,
      span.begin
    );
    if (!step) {
      // Outline nodes for timeline-derived span rows carry the SPAN id
      // (collectRawEvents re-emits span_begin with uuid = timeline span id),
      // so anchor lookups arrive keyed by span id, not synthetic uuid.
      ordinals.set(span.id, span.begin);
    }
    emitStrays(spanIdx, loUs);
    emitContents(spanIdx, loUs, hiUs);
    push(
      synth(
        step
          ? {
              ...stepFields("end"),
              ...common(endUuid, realSpanId(span.parent ?? null), hiUs),
            }
          : {
              event: "span_end",
              id: span.id,
              ...common(endUuid, span.id, hiUs),
            }
      ),
      endUuid,
      span.extent[1]
    );
  };

  const rootLoUs = spans.length > 0 ? beginUs(0) : 0;
  const rootHiUs = spans.reduce(
    (acc, _, index) => Math.max(acc, endUs(index)),
    rootLoUs
  );
  emitContents(null, rootLoUs, rootHiUs);

  return { events, ordinals };
};
